import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TranslateIcon from '@mui/icons-material/Translate';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import translationEN from '../../locales/en/translation.json';
import translationESMX from '../../locales/es-MX/translation.json';

// -----------------------------------------------------
// Client-side translations audit report.
// The translations are already bundled into the frontend,
// so we build the HTML report right here in the browser
// and open it in a new tab. The user can then use the
// browser's native Print → Save as PDF. No server load,
// no Heroku timeout, no Chromium.
// -----------------------------------------------------

function flattenTranslations(obj, prefix = '', out = {}) {
  if (obj == null || typeof obj !== 'object' || Array.isArray(obj)) {
    out[prefix] = Array.isArray(obj) ? JSON.stringify(obj) : obj == null ? '' : String(obj);
    return out;
  }
  for (const key of Object.keys(obj)) {
    const next = prefix ? `${prefix}.${key}` : key;
    const value = obj[key];
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      flattenTranslations(value, next, out);
    } else {
      out[next] = Array.isArray(value) ? JSON.stringify(value) : value == null ? '' : String(value);
    }
  }
  return out;
}

function escapeHtml(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function buildReportHtml() {
  const enFlat = flattenTranslations(translationEN);
  const esFlat = flattenTranslations(translationESMX);

  const allKeys = Array.from(new Set([...Object.keys(enFlat), ...Object.keys(esFlat)])).sort();

  const grouped = {};
  let missingEs = 0;
  let missingEn = 0;
  let identical = 0;
  for (const key of allKeys) {
    const section = key.includes('.') ? key.split('.')[0] : '(root)';
    const en = Object.prototype.hasOwnProperty.call(enFlat, key) ? enFlat[key] : null;
    const es = Object.prototype.hasOwnProperty.call(esFlat, key) ? esFlat[key] : null;
    if (en == null || en === '') missingEn++;
    if (es == null || es === '') missingEs++;
    if (en != null && es != null && en === es && en.trim() !== '') identical++;
    if (!grouped[section]) grouped[section] = [];
    grouped[section].push({ key, en, es });
  }

  const totalKeys = allKeys.length;
  const generatedAt = new Date().toLocaleString();
  const sectionNames = Object.keys(grouped).sort();

  let rowsHtml = '';
  for (const section of sectionNames) {
    rowsHtml += `
      <tr class="section-row">
        <td colspan="3">${escapeHtml(section)} <span class="section-count">(${grouped[section].length})</span></td>
      </tr>
    `;
    for (const entry of grouped[section]) {
      const enMissing = entry.en == null || entry.en === '';
      const esMissing = entry.es == null || entry.es === '';
      const sameText = !enMissing && !esMissing && entry.en === entry.es && entry.en.trim() !== '';

      const classes = [];
      if (enMissing) classes.push('missing-en');
      if (esMissing) classes.push('missing-es');
      if (sameText) classes.push('same-text');

      rowsHtml += `
        <tr class="${classes.join(' ')}">
          <td class="col-en">${enMissing ? '<em>— missing —</em>' : escapeHtml(entry.en)}</td>
          <td class="col-es">${esMissing ? '<em>— missing —</em>' : escapeHtml(entry.es)}</td>
          <td class="col-key"><code>${escapeHtml(entry.key)}</code></td>
        </tr>
      `;
    }
  }

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Translations Audit — EN &amp; ES-MX</title>
  <style>
    @page { size: Letter; margin: 0.5in; }
    * { box-sizing: border-box; }
    body {
      font-family: "Segoe UI", Arial, sans-serif;
      font-size: 11px;
      color: #222;
      margin: 0;
      padding: 20px 24px 40px;
      background: #f5f5f5;
    }
    .sheet {
      background: white;
      max-width: 1100px;
      margin: 0 auto;
      padding: 24px 28px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    }

    .toolbar {
      max-width: 1100px;
      margin: 0 auto 14px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
    }
    .toolbar .hint { color: #555; font-size: 12px; }
    .toolbar button {
      background: #f57c00;
      color: white;
      border: none;
      padding: 10px 18px;
      font-size: 13px;
      font-weight: bold;
      border-radius: 4px;
      cursor: pointer;
    }
    .toolbar button:hover { background: #ef6c00; }
    .toolbar button:focus-visible { outline: 3px solid #ffc080; outline-offset: 2px; }

    .header {
      background: #f57c00;
      color: white;
      padding: 14px 18px;
      margin-bottom: 14px;
      border-radius: 4px;
    }
    .header h1 { margin: 0 0 4px 0; font-size: 18px; }
    .header p { margin: 0; font-size: 11px; opacity: 0.95; }

    .summary {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-bottom: 14px;
    }
    .stat {
      background: #fafafa;
      border: 1px solid #e0e0e0;
      border-radius: 4px;
      padding: 6px 10px;
      font-size: 11px;
    }
    .stat .label { color: #666; margin-right: 4px; }
    .stat .value { font-weight: bold; color: #222; }
    .stat.warn .value { color: #d32f2f; }
    .stat.info .value { color: #1976d2; }

    .legend {
      font-size: 10px;
      color: #555;
      margin-bottom: 10px;
    }
    .legend .chip {
      display: inline-block;
      padding: 2px 6px;
      border-radius: 3px;
      margin-right: 6px;
      border: 1px solid #ddd;
    }
    .legend .chip.missing-es { background: #fff3e0; }
    .legend .chip.missing-en { background: #ffebee; }
    .legend .chip.same-text { background: #fffde7; }

    table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
    }
    thead { display: table-header-group; }
    th, td {
      border: 1px solid #ddd;
      padding: 6px 8px;
      vertical-align: top;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }
    th {
      background: #eeeeee;
      text-align: left;
      font-size: 11px;
      font-weight: bold;
    }
    tbody tr { page-break-inside: avoid; }

    .col-en { width: 36%; }
    .col-es { width: 36%; }
    .col-key { width: 28%; font-family: "Consolas", "Courier New", monospace; font-size: 10px; color: #555; }
    .col-key code { font-family: inherit; }

    tr.section-row td {
      background: #1976d2;
      color: white;
      font-weight: bold;
      font-size: 12px;
      padding: 6px 8px;
      border: 1px solid #1976d2;
    }
    tr.section-row .section-count { font-weight: normal; opacity: 0.85; font-size: 11px; }

    tr.missing-es { background: #fff3e0; }
    tr.missing-en { background: #ffebee; }
    tr.same-text { background: #fffde7; }
    tr.missing-es.missing-en { background: #ffcdd2; }

    em { color: #d32f2f; font-style: italic; }

    @media print {
      body { background: white; padding: 0; }
      .sheet { box-shadow: none; max-width: none; padding: 0; margin: 0; }
      .toolbar { display: none !important; }
    }
  </style>
</head>
<body>
  <div class="toolbar" aria-hidden="false">
    <div class="hint">
      Use your browser's <strong>Print</strong> feature and choose <strong>Save as PDF</strong> to export this report.
      <br />
      Usa la función <strong>Imprimir</strong> de tu navegador y elige <strong>Guardar como PDF</strong> para exportar este reporte.
    </div>
    <button type="button" onclick="window.print()" aria-label="Print or save as PDF">
      Print / Save as PDF
    </button>
  </div>

  <div class="sheet">
    <div class="header">
      <h1>Translations Audit Report — EN &amp; ES-MX</h1>
      <p>Generated ${escapeHtml(generatedAt)} · For internal translator review</p>
    </div>

    <div class="summary">
      <div class="stat info"><span class="label">Total keys:</span><span class="value">${totalKeys}</span></div>
      <div class="stat info"><span class="label">Sections:</span><span class="value">${sectionNames.length}</span></div>
      <div class="stat ${missingEs ? 'warn' : ''}"><span class="label">Missing in ES-MX:</span><span class="value">${missingEs}</span></div>
      <div class="stat ${missingEn ? 'warn' : ''}"><span class="label">Missing in EN:</span><span class="value">${missingEn}</span></div>
      <div class="stat"><span class="label">Identical EN/ES (possibly untranslated):</span><span class="value">${identical}</span></div>
    </div>

    <div class="legend">
      <span class="chip missing-es">Missing Spanish</span>
      <span class="chip missing-en">Missing English</span>
      <span class="chip same-text">Identical in both (likely untranslated)</span>
    </div>

    <table>
      <thead>
        <tr>
          <th class="col-en">English (EN)</th>
          <th class="col-es">Spanish (ES-MX)</th>
          <th class="col-key">Key</th>
        </tr>
      </thead>
      <tbody>
        ${rowsHtml}
      </tbody>
    </table>
  </div>
</body>
</html>`;
}

const TranslationsReport = () => {
  const { t } = useTranslation();
  const [error, setError] = useState(null);

  const openReport = () => {
    setError(null);
    try {
      const html = buildReportHtml();
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) {
        setError(
          t(
            'reports.translationsAudit.popupBlocked',
            'Your browser blocked the new tab. Please allow popups for this site and try again.'
          )
        );
        URL.revokeObjectURL(url);
        return;
      }
      // Release the Blob URL once the new tab has had time to load it.
      // 60 seconds is plenty and avoids leaking the buffer indefinitely.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.error('Error building translations report:', err);
      setError(
        t('reports.translationsAudit.error', 'Failed to build the translations report. Please try again.')
      );
    }
  };

  return (
    <Box>
      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
        >
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <TranslateIcon sx={{ color: '#f57c00' }} fontSize="large" />
              <Typography variant="h5" fontWeight="bold">
                {t('reports.translationsAudit.title', 'Translations Audit')}
              </Typography>
            </Stack>
            <Typography variant="body1" color="text.secondary">
              {t(
                'reports.translationsAudit.description',
                'Opens a printable, color-coded report in a new tab with every translation key side-by-side in English and Mexican Spanish. Use your browser\'s Print → Save as PDF to hand it off to an internal translator.'
              )}
            </Typography>
          </Box>

          <Button
            variant="contained"
            size="large"
            startIcon={<OpenInNewIcon />}
            onClick={openReport}
            aria-label={t('reports.translationsAudit.openAria', 'Open translations audit report in a new tab')}
            sx={{ bgcolor: '#f57c00', '&:hover': { bgcolor: '#ef6c00' }, minWidth: 240 }}
          >
            {t('reports.translationsAudit.open', 'Open Printable Report')}
          </Button>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: { xs: 2, md: 3 } }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
          {t('reports.translationsAudit.whatsIncluded', "What's included in the report")}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <List dense>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <CheckCircleIcon sx={{ color: '#2e7d32' }} />
            </ListItemIcon>
            <ListItemText
              primary={t(
                'reports.translationsAudit.feature1',
                'Every translation key used in the app, grouped by section (common, auth, dashboard, …).'
              )}
            />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <CheckCircleIcon sx={{ color: '#2e7d32' }} />
            </ListItemIcon>
            <ListItemText
              primary={t(
                'reports.translationsAudit.feature2',
                'English (EN) text alongside Mexican Spanish (ES-MX) text, with the internal key name for reference.'
              )}
            />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <CheckCircleIcon sx={{ color: '#2e7d32' }} />
            </ListItemIcon>
            <ListItemText
              primary={t(
                'reports.translationsAudit.feature3',
                'Highlighted rows for keys that are missing in one language or that are identical in both (likely untranslated).'
              )}
            />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <CheckCircleIcon sx={{ color: '#2e7d32' }} />
            </ListItemIcon>
            <ListItemText
              primary={t(
                'reports.translationsAudit.feature4',
                'A summary header with totals: total keys, missing in each language, and identical EN/ES entries.'
              )}
            />
          </ListItem>
        </List>
      </Paper>
    </Box>
  );
};

export default TranslationsReport;
